import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample post content for display
const samplePostContent = {
  "career-changes-30s-40s": {
    title: "Navigating Career Changes in Your 30s and 40s",
    content: `
## The Unique Challenge of Mid-Career Transitions

Career changes in your 30s and 40s come with a unique set of challenges that younger professionals don't face. You have more responsibilities: mortgages, families, established lifestyles. But you also have something invaluable: experience.

### Why Mid-Career Changes Are Different

**Financial Obligations**: Unlike your 20s, you likely have significant financial commitments that make "taking a leap" feel more risky.

**Identity Tied to Career**: After 10-20 years in a field, your professional identity can feel inseparable from your job title.

**The "Too Late" Myth**: Society often tells us that major changes should happen earlier, creating unnecessary pressure and doubt.

### What Actually Works

1. **Leverage Your Experience**: Your transferable skills are more valuable than you think. Leadership, problem-solving, and domain expertise translate across industries.

2. **Build Before You Leap**: The most successful career changers start building their new path while still employed.

3. **Seek Coordinated Support**: Trying to manage emotional readiness, career exploration, and logistics alone is overwhelming. This is where comprehensive support makes the difference.

### The Become Approach

At Become, we understand that mid-career transitions require a different approach. Your coordinated support team works together to address the emotional, practical, and logistical aspects of your transition, all at once.

You don't have to choose between processing your feelings and planning your next move. You can do both, with support that understands where you are in life.
    `,
    category: "Career Transitions",
    image_url: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200",
    created_at: new Date().toISOString()
  },
  "emotional-readiness-change": {
    title: "Building Emotional Readiness for Change",
    content: `
## The Foundation of Successful Change

Before you can change your external circumstances, you need to prepare internally. This isn't just feel-good advice. It's backed by decades of research.

### What Is Emotional Readiness?

Emotional readiness is your psychological capacity to engage with and navigate change. It includes:

- **Self-awareness**: Understanding your emotional patterns and triggers
- **Resilience**: The ability to recover from setbacks
- **Openness**: Willingness to embrace uncertainty
- **Regulation**: Managing stress and anxiety effectively

### The Science Behind Readiness

Research in clinical psychology shows that attempting major life changes before achieving emotional readiness often leads to:

- Higher rates of burnout and giving up
- Decision-making driven by fear rather than values
- Difficulty maintaining new habits and behaviors
- Increased anxiety and stress

### Building Your Foundation

**1. Process What You're Leaving Behind**
Every transition involves loss, even positive ones. Acknowledging what you're leaving creates space for what's coming.

**2. Identify Your Support System**
Who will be there for you during this transition? If the answer is "no one," that's okay, but it means building support should be your first priority.

**3. Develop Emotional Tools**
Journaling, mindfulness, and professional support all contribute to emotional readiness.

### How Become Helps

Your Emotional Readiness Guide is designed to help you build this foundation. Drawing on clinically informed frameworks, it helps you process, prepare, and progress at your own pace.
    `,
    category: "Emotional Readiness",
    image_url: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200",
    created_at: new Date(Date.now() - 86400000).toISOString()
  },
  "hidden-costs-transitions": {
    title: "The Hidden Costs of Unsupported Transitions",
    content: `
## Beyond the Dollar Signs

When we talk about the cost of career transitions, we usually focus on money: coaching fees, training programs, potential income loss. But the hidden costs of unsupported transitions extend far beyond your bank account.

### The Health Impact

Studies show that unsupported major life transitions correlate with:

- Increased rates of anxiety and depression
- Higher cortisol levels and chronic stress
- Sleep disruption and fatigue
- Weakened immune function

### The Relationship Cost

Transitions don't happen in isolation. Unsupported changes affect:

- **Partners and Spouses**: Who often bear the emotional load
- **Children**: Who sense parental stress and uncertainty
- **Friendships**: Which can strain under the weight of constant processing

### The Opportunity Cost

Perhaps the most significant hidden cost is what doesn't happen:

- Careers that could have flourished with proper support
- Transitions that get abandoned halfway through
- Potential that remains unrealized

### Why Coordination Matters

The research is clear: coordinated support produces better outcomes than fragmented help. When emotional support, career guidance, resource navigation, and change management work together, transitions succeed at much higher rates.

### A Different Approach

This is why Become was built. Not to be another tool in your growing collection, but to be the support system that makes real change accessible to everyone, not just those who can afford $3,000 to $10,000 per month in professional services.
    `,
    category: "Research & Insights",
    image_url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200",
    created_at: new Date(Date.now() - 172800000).toISOString()
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      // Use sample content if post not found in DB
      if (samplePostContent[slug]) {
        setPost(samplePostContent[slug]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-bone pt-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-warm-bone pt-20">
        <h1 className="font-heading text-3xl text-text-dark mb-4">Post Not Found</h1>
        <Link to="/blog" className="text-brand-primary hover:underline">
          Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex items-end hero-dark" data-testid="post-hero">
        <div className="absolute inset-0">
          <img
            src={post.image_url || "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200"}
            alt={post.title}
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-deep-violet via-deep-violet/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-brand-peach hover:text-brand-lavender transition-colors mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="inline-flex items-center gap-2 text-brand-peach text-sm">
                <Tag className="w-4 h-4" />
                {post.category}
              </span>
              <span className="inline-flex items-center gap-2 text-text-light/50 text-sm">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-text-light leading-tight" data-testid="post-title">
              {post.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-warm-bone" data-testid="post-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            <div 
              className="text-text-dark/80 leading-relaxed space-y-6"
              dangerouslySetInnerHTML={{ 
                __html: post.content
                  .replace(/^## (.+)$/gm, '<h2 class="font-heading text-2xl text-text-dark mt-10 mb-4">$1</h2>')
                  .replace(/^### (.+)$/gm, '<h3 class="font-heading text-xl text-text-dark mt-8 mb-3">$1</h3>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-text-dark">$1</strong>')
                  .replace(/^- (.+)$/gm, '<li class="text-text-dark/70">$1</li>')
                  .replace(/(<li.*<\/li>\n)+/g, '<ul class="list-disc pl-6 space-y-2 my-4">$&</ul>')
                  .replace(/^\d+\. (.+)$/gm, '<li class="text-text-dark/70">$1</li>')
                  .replace(/\n\n/g, '</p><p class="text-text-dark/70">')
              }}
            />
          </motion.article>

          {/* Share & Back */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-brand-primary hover:text-brand-primary/80 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
